'use client';

import { c as _c } from "react/compiler-runtime";
import { formatCents, isNotNullOrUndefined } from 'betterplace-utils';
import { Button } from 'design-system/client';
import { useTranslations, useLocale } from 'next-intl';
import { useRef, useState } from 'react';
import { getDataCssVariables } from '../helpers';
import ScreenName from '../ScreenName';
import StreambotWrapper, { type StreambotWrapperProps } from '../StreambotWrapper';
import type { StreambotComponentProps } from '../types';
import type { DonationAlertWidgetSettings } from './donationAlertWidgetSettingsSchema';
import getDefaultValues from './getDefaultValues';
import { useApiV4WithDemoData } from '@/api';
import GifAndSound, { type GifAndSoundProps } from '@/components/GifAndSound';
import type { Maybe } from '@/types';
export type DonationAlertData = {
  id: number;
  authorName: Maybe<string>;
  donatedAmountInCents: Maybe<number>;
};
const defaultDemoData = {
  id: 1,
  donatedAmountInCents: 1337,
  authorName: 'Unicorn&lt;3'
} satisfies DonationAlertData;
export type DonationAlertProps = StreambotComponentProps<DonationAlertWidgetSettings, DonationAlertData>;
type DonationAlertContentProps = Required<Omit<DonationAlertProps, keyof StreambotWrapperProps | 'darkMode' | 'gifName' | 'mp3Name'>>;
function useData(fundraisingEventId, t0) {
  const $ = _c(14);
  const demoData_ = t0 === undefined ? null : t0;
  const locale = useLocale();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [queue, setQueue] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const queueSetRef = useRef(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (...t4) => {
      const args = t4;
      const newDonations = [];
      for (const donation of args) {
        const shouldBeAddedToTheQueue = !queueSetRef.current.has(donation.id) && (donation.authorName || donation.donatedAmountInCents);
        if (!shouldBeAddedToTheQueue) {
          continue;
        }
        newDonations.push(donation);
        queueSetRef.current.add(donation.id);
      }
      if (!newDonations.length) {
        return;
      }
      setQueue(old => [...old, ...newDonations]);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const pushToQueue = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = id => {
      setQueue(old_0 => {
        const [first, ...rest] = old_0;
        if (!first) {
          return old_0;
        }
        if (id !== first.id) {
          return old_0;
        }
        queueSetRef.current.delete(first.id);
        return rest;
      });
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const removeFirstFromQueue = t4;
  let t5;
  if ($[4] !== demoData_) {
    t5 = demoData_ ? [demoData_] : undefined;
    $[4] = demoData_;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const demoData = t5;
  let t6;
  if ($[6] !== locale) {
    t6 = {
      locale
    };
    $[6] = locale;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      after(param) {
        if (!param) {
          return;
        }
        pushToQueue(...param.data.map(_temp));
      },
      intervalMs: 5000
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}/opinions.json?order=id:desc&per_page=1&page=1`, t6, t7, demoData);
  let result;
  if ($[9] !== t8) {
    const {
      data: data_,
      ...t9
    } = t8;
    result = t9;
    $[9] = t8;
    $[10] = result;
  } else {
    result = $[10];
  }
  let t9;
  if ($[11] !== queue[0] || $[12] !== result) {
    t9 = {
      data: queue[0],
      removeFirstFromQueue,
      ...result
    };
    $[11] = queue[0];
    $[12] = result;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}
function _temp(t0) {
  const {
    id: id_0,
    author,
    donated_amount_in_cents
  } = t0;
  return {
    id: id_0,
    authorName: author?.name,
    donatedAmountInCents: donated_amount_in_cents
  };
}
function DonationAlertComponent(t0) {
  const $ = _c(31);
  const {
    id,
    duration,
    mp3,
    volume,
    donatedAmountInCents,
    authorName,
    gif,
    gifHeight,
    headlineText,
    dataFontColor,
    dataFont,
    dataFontSize,
    dataFontWeight,
    onPlaybackComplete,
    onPlaybackStart,
    playWithSound
  } = t0;
  const locale = useLocale();
  let t1;
  if ($[0] !== dataFont || $[1] !== dataFontColor || $[2] !== dataFontSize || $[3] !== dataFontWeight) {
    t1 = getDataCssVariables(dataFont, dataFontSize, dataFontWeight, dataFontColor);
    $[0] = dataFont;
    $[1] = dataFontColor;
    $[2] = dataFontSize;
    $[3] = dataFontWeight;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const dataCssVariables = t1;
  const t = useTranslations("nextjs");
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <br />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== donatedAmountInCents || $[7] !== locale || $[8] !== t) {
    t3 = donatedAmountInCents ? formatCents(locale, donatedAmountInCents) : t("streambot.amount_fallback");
    $[6] = donatedAmountInCents;
    $[7] = locale;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t) {
    t4 = t("streambot.from");
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== authorName) {
    t5 = <ScreenName authorName={authorName} />;
    $[12] = authorName;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== dataCssVariables || $[15] !== headlineText || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t6 = <div className="donationAlert" style={dataCssVariables}>{headlineText}{t2}{t3}{t4}{t5}</div>;
    $[14] = dataCssVariables;
    $[15] = headlineText;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== duration || $[21] !== gif || $[22] !== gifHeight || $[23] !== id || $[24] !== mp3 || $[25] !== onPlaybackComplete || $[26] !== onPlaybackStart || $[27] !== playWithSound || $[28] !== t6 || $[29] !== volume) {
    t7 = <GifAndSound id={id} gifHeight={gifHeight} gifSrc={gif} duration={duration} soundSrc={mp3} volume={volume} onPlaybackComplete={onPlaybackComplete} onPlaybackStart={onPlaybackStart} playWithSound={playWithSound}>{t6}</GifAndSound>;
    $[20] = duration;
    $[21] = gif;
    $[22] = gifHeight;
    $[23] = id;
    $[24] = mp3;
    $[25] = onPlaybackComplete;
    $[26] = onPlaybackStart;
    $[27] = playWithSound;
    $[28] = t6;
    $[29] = volume;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  return t7;
}
function DonationAlertContent(t0) {
  const $ = _c(31);
  let demoData;
  let fundraisingEventId;
  let props;
  let withDemoData;
  if ($[0] !== t0) {
    ({
      fundraisingEventId,
      demoData,
      withDemoData,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = demoData;
    $[2] = fundraisingEventId;
    $[3] = props;
    $[4] = withDemoData;
  } else {
    demoData = $[1];
    fundraisingEventId = $[2];
    props = $[3];
    withDemoData = $[4];
  }
  const [demoDataId, setDemoDataId] = useState(null);
  const [demoInProgress, setDemoInProgress] = useState(false);
  let t1;
  if ($[5] !== withDemoData) {
    t1 = () => {
      if (!withDemoData) {
        return;
      }
      setDemoInProgress(true);
    };
    $[5] = withDemoData;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const onPlaybackStart = t1;
  let t2;
  if ($[7] !== onPlaybackStart || $[8] !== withDemoData) {
    t2 = () => {
      if (!withDemoData) {
        return;
      }
      onPlaybackStart();
      setDemoDataId(_temp2);
    };
    $[7] = onPlaybackStart;
    $[8] = withDemoData;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const startDemo = t2;
  const stopDemo = id => {
    setDemoInProgress(false);
    removeFirstFromQueue(id);
  };
  const t = useTranslations("nextjs.streambot.live_widgets.donation_alert");
  let t3;
  if ($[10] !== demoData || $[11] !== demoDataId) {
    t3 = isNotNullOrUndefined(demoDataId) ? {
      ...demoData,
      id: demoDataId
    } : null;
    $[10] = demoData;
    $[11] = demoDataId;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const variableDemoData = t3;
  const {
    data: data_,
    removeFirstFromQueue: t4
  } = useData(fundraisingEventId, withDemoData ? variableDemoData : undefined);
  const removeFirstFromQueue = t4;
  let t5;
  if ($[13] !== data_ || $[14] !== variableDemoData || $[15] !== withDemoData) {
    t5 = withDemoData ? variableDemoData : data_ ?? {};
    $[13] = data_;
    $[14] = variableDemoData;
    $[15] = withDemoData;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const data = t5;
  let t6;
  if ($[17] !== data || $[18] !== onPlaybackStart || $[19] !== props || $[20] !== stopDemo || $[21] !== withDemoData) {
    t6 = <DonationAlertComponent {...props} {...data} withDemoData={withDemoData} onPlaybackComplete={stopDemo} onPlaybackStart={onPlaybackStart} />;
    $[17] = data;
    $[18] = onPlaybackStart;
    $[19] = props;
    $[20] = stopDemo;
    $[21] = withDemoData;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== demoInProgress || $[24] !== startDemo || $[25] !== t || $[26] !== withDemoData) {
    t7 = withDemoData && !demoInProgress && <Button kind="secondary" type="button" onClick={startDemo} style={{
      position: "absolute",
      inset: 0,
      margin: "auto",
      inlineSize: "fit-content",
      blockSize: "fit-content"
    }}>{t("play_demo")}</Button>;
    $[23] = demoInProgress;
    $[24] = startDemo;
    $[25] = t;
    $[26] = withDemoData;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t6 || $[29] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}
function _temp2(prev) {
  return prev ? prev + 1 : 1;
}
function DonationAlert(t0) {
  const $ = _c(36);
  let demoData;
  let fundraisingEventId;
  let t1;
  let withDemoData;
  if ($[0] !== t0) {
    const {
      fundraisingEventId: t2,
      demoData: t3,
      withDemoData: t4,
      ...props
    } = t0;
    fundraisingEventId = t2;
    demoData = t3 === undefined ? defaultDemoData : t3;
    withDemoData = t4 === undefined ? false : t4;
    t1 = getDefaultValues(props);
    $[0] = t0;
    $[1] = demoData;
    $[2] = fundraisingEventId;
    $[3] = t1;
    $[4] = withDemoData;
  } else {
    demoData = $[1];
    fundraisingEventId = $[2];
    t1 = $[3];
    withDemoData = $[4];
  }
  const mergedProps = t1;
  let dataFont;
  let dataFontColor;
  let dataFontSize;
  let dataFontWeight;
  let duration;
  let gif;
  let gifHeight;
  let headlineText;
  let mp3;
  let playWithSound;
  let volume;
  let wrapperProps;
  if ($[5] !== mergedProps) {
    ({
      headlineText,
      dataFontColor,
      dataFontWeight,
      dataFont,
      dataFontSize,
      gif,
      gifHeight,
      volume,
      mp3,
      duration,
      playWithSound,
      ...wrapperProps
    } = mergedProps);
    $[5] = mergedProps;
    $[6] = dataFont;
    $[7] = dataFontColor;
    $[8] = dataFontSize;
    $[9] = dataFontWeight;
    $[10] = duration;
    $[11] = gif;
    $[12] = gifHeight;
    $[13] = headlineText;
    $[14] = mp3;
    $[15] = playWithSound;
    $[16] = volume;
    $[17] = wrapperProps;
  } else {
    dataFont = $[6];
    dataFontColor = $[7];
    dataFontSize = $[8];
    dataFontWeight = $[9];
    duration = $[10];
    gif = $[11];
    gifHeight = $[12];
    headlineText = $[13];
    mp3 = $[14];
    playWithSound = $[15];
    volume = $[16];
    wrapperProps = $[17];
  }
  let t2;
  if ($[18] !== dataFont || $[19] !== dataFontColor || $[20] !== dataFontSize || $[21] !== dataFontWeight || $[22] !== demoData || $[23] !== duration || $[24] !== fundraisingEventId || $[25] !== gif || $[26] !== gifHeight || $[27] !== headlineText || $[28] !== mp3 || $[29] !== playWithSound || $[30] !== volume || $[31] !== withDemoData) {
    t2 = <DonationAlertContent fundraisingEventId={fundraisingEventId} demoData={demoData} withDemoData={withDemoData} gif={gif} gifHeight={gifHeight} mp3={mp3} volume={volume} duration={duration} headlineText={headlineText} dataFont={dataFont} dataFontColor={dataFontColor} dataFontSize={dataFontSize} dataFontWeight={dataFontWeight} playWithSound={playWithSound} />;
    $[18] = dataFont;
    $[19] = dataFontColor;
    $[20] = dataFontSize;
    $[21] = dataFontWeight;
    $[22] = demoData;
    $[23] = duration;
    $[24] = fundraisingEventId;
    $[25] = gif;
    $[26] = gifHeight;
    $[27] = headlineText;
    $[28] = mp3;
    $[29] = playWithSound;
    $[30] = volume;
    $[31] = withDemoData;
    $[32] = t2;
  } else {
    t2 = $[32];
  }
  let t3;
  if ($[33] !== t2 || $[34] !== wrapperProps) {
    t3 = <StreambotWrapper {...wrapperProps}>{t2}</StreambotWrapper>;
    $[33] = t2;
    $[34] = wrapperProps;
    $[35] = t3;
  } else {
    t3 = $[35];
  }
  return t3;
}
export default DonationAlert;